import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["phoneInput", "userSelect", "expiryInput", "list", "emptyState", "groupContent", "expandIcon"]
  static values = {
    realEstateAdminId: String,
    rotationId: String,
    rotationResource: { type: String, default: 'lead_assignment_rotations' }
  }

  connect() {
    this.csrfToken = document.querySelector('meta[name="csrf-token"]').content
  }

  toggleGroup(event) {
    const groupContent = event.currentTarget.nextElementSibling
    this.setGroupExpanded(event.currentTarget, groupContent.style.display === 'none')
  }

  expandGroup(group) {
    this.setGroupExpanded(group.querySelector('button'), true)
  }

  setGroupExpanded(button, expanded) {
    const groupContent = button.nextElementSibling
    const expandIcon = button.querySelector('[data-client-assignments-target="expandIcon"]')

    groupContent.style.display = expanded ? 'block' : 'none'
    if (expandIcon) {
      expandIcon.style.transform = expanded ? 'rotate(180deg)' : 'rotate(0deg)'
    }
  }

  async create(event) {
    event.preventDefault()

    const phone = this.phoneInputTarget.value.trim()
    const userId = this.userSelectTarget.value
    const expiryDate = this.expiryInputTarget.value

    if (!phone) {
      this.showError('Por favor, informe o telefone do cliente.')
      return
    }

    if (!userId) {
      this.showError('Por favor, selecione um corretor.')
      return
    }

    const payload = {
      client_phone: phone,
      user_id: userId
    }

    // Add expiry date if provided
    if (expiryDate) {
      payload.expires_at = expiryDate
    }

    try {
      const response = await fetch(
        `/real_estate_admin/${this.realEstateAdminIdValue}/${this.rotationResourceValue}/${this.rotationIdValue}/create_client_assignment`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': this.csrfToken
          },
          body: JSON.stringify(payload)
        }
      )

      const data = await response.json()

      if (data.status === 'success') {
        this.showSuccess(data.message)

        // Clear form
        this.phoneInputTarget.value = ''
        this.userSelectTarget.selectedIndex = 0
        this.expiryInputTarget.value = ''

        // Hide empty state if it exists
        if (this.hasEmptyStateTarget) {
          this.emptyStateTarget.style.display = 'none'
        }

        // Add new assignment to the list dynamically
        this.addAssignmentToList(data.assignment)
      } else {
        this.showError(data.message)
      }
    } catch (error) {
      console.error('Error creating client assignment:', error)
      this.showError('Erro ao criar atribuição. Por favor, tente novamente.')
    }
  }

  addAssignmentToList(assignment) {
    // Create the list container if it doesn't exist
    if (!this.hasListTarget) {
      const container = this.element.querySelector('[data-client-assignments-target="emptyState"]').parentElement
      const listHtml = '<div class="space-y-3" data-client-assignments-target="list"></div>'
      container.innerHTML = listHtml
    }

    // Check if there's already a group for this broker
    const existingGroups = this.listTarget.querySelectorAll('.border.border-gray-200.rounded-lg')
    let brokerGroup = null

    existingGroups.forEach(group => {
      const groupContent = group.querySelector('[data-client-assignments-target="groupContent"]')
      const firstAssignment = groupContent ? groupContent.querySelector('[data-assignment-id]') : null
      if (firstAssignment) {
        const assignmentRow = firstAssignment
        // We need to check if this group belongs to the same broker by checking user_id
        // We'll use a data attribute to track this
        if (group.dataset.userId === assignment.user_id.toString()) {
          brokerGroup = group
        }
      }
    })

    const userName = assignment.user_name
    const expiryText = assignment.expires_at ? `Expira: ${assignment.expires_at}` : 'Nunca expira'
    const initials = userName.substring(0, 2).toUpperCase()

    if (brokerGroup) {
      // Add to existing group
      const groupContent = brokerGroup.querySelector('[data-client-assignments-target="groupContent"]')
      const assignmentRowHtml = this.createAssignmentRowHtml(assignment, expiryText)
      groupContent.insertAdjacentHTML('afterbegin', assignmentRowHtml)
      this.expandGroup(brokerGroup)

      // Update the count in the header
      const countElement = brokerGroup.querySelector('.text-sm.text-gray-500')
      if (countElement) {
        const currentCount = groupContent.querySelectorAll('div[data-assignment-id]').length
        const phoneText = currentCount === 1 ? 'telefone atribuído' : 'telefones atribuídos'
        countElement.textContent = `${currentCount} ${phoneText}`
      }

      // Update active count badge
      const badgeContainer = brokerGroup.querySelector('.bg-black.text-white')
      const allAssignments = groupContent.querySelectorAll('div[data-assignment-id]')
      let activeCount = 0

      // Count enabled assignments (those without opacity-50 class)
      allAssignments.forEach(el => {
        if (!el.classList.contains('opacity-50')) {
          activeCount++
        }
      })

      if (activeCount > 0) {
        if (badgeContainer) {
          badgeContainer.textContent = `${activeCount} ativo${activeCount === 1 ? '' : 's'}`
        } else {
          // Create badge if it doesn't exist
          const badgeHtml = `<span class="bg-black text-white text-xs font-bold px-2.5 py-1 rounded-full">${activeCount} ativo${activeCount === 1 ? '' : 's'}</span>`
          const expandIcon = brokerGroup.querySelector('[data-client-assignments-target="expandIcon"]')
          if (expandIcon) {
            expandIcon.insertAdjacentHTML('beforebegin', badgeHtml)
          }
        }
      } else if (badgeContainer) {
        // Remove badge if no active assignments
        badgeContainer.remove()
      }
    } else {
      // Create new group
      const groupHtml = this.createBrokerGroupHtml(assignment, userName, initials, expiryText)
      this.listTarget.insertAdjacentHTML('afterbegin', groupHtml)
    }

    // Scroll to the list
    this.listTarget.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }

  createBrokerGroupHtml(assignment, userName, initials, expiryText) {
    const activeCount = assignment.enabled ? 1 : 0
    const activeBadge = activeCount > 0 ? `<span class="bg-black text-white text-xs font-bold px-2.5 py-1 rounded-full">${activeCount} ativo</span>` : ''

    return `
      <div class="border border-gray-200 rounded-lg overflow-hidden animate-slide-in" data-user-id="${assignment.user_id}">
        <!-- Collapsible header -->
        <button class="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors" type="button" data-action="click->client-assignments#toggleGroup">
          <!-- Left: Broker info -->
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 bg-yellow-500 rounded-full flex items-center justify-center text-black text-xs font-medium">
              ${initials}
            </div>
            <div class="text-left">
              <div class="font-semibold text-gray-900">${userName}</div>
              <div class="text-sm text-gray-500">1 telefone atribuído</div>
            </div>
          </div>

          <!-- Right: Active count badge + expand icon -->
          <div class="flex items-center gap-3">
            ${activeBadge}
            <svg class="w-5 h-5 text-gray-400 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-client-assignments-target="expandIcon">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
            </svg>
          </div>
        </button>

        <!-- Expandable content -->
        <div class="divide-y divide-gray-100" data-client-assignments-target="groupContent">
          ${this.createAssignmentRowHtml(assignment, expiryText)}
        </div>
      </div>
    `
  }

  createAssignmentRowHtml(assignment, expiryText) {
    const isEnabled = assignment.enabled !== false // Default to true if not specified
    const opacityClass = isEnabled ? '' : 'opacity-50'
    const checkedAttr = isEnabled ? 'checked' : ''

    return `
      <div class="p-3 bg-white hover:bg-gray-50 transition-colors flex items-center justify-between animate-slide-in ${opacityClass}" data-assignment-id="${assignment.id}">
        <!-- Phone + expiry -->
        <div class="flex items-center gap-4 flex-1">
          <div class="flex items-center gap-2">
            <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
            </svg>
            <span class="font-medium text-gray-900">${assignment.formatted_phone}</span>
          </div>

          <!-- Expiration info -->
          <div class="flex items-center gap-1.5">
            <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
            </svg>
            <span class="text-xs text-gray-600">${expiryText}</span>
          </div>
        </div>

        <!-- Actions (toggle + delete) -->
        <div class="flex items-center gap-2">
          <!-- Enable/disable toggle -->
          <label class="relative inline-flex items-center cursor-pointer">
            <input type="checkbox" class="sr-only peer" ${checkedAttr} data-action="change->client-assignments#toggle" data-assignment-id="${assignment.id}">
            <div class="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-yellow-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-yellow-500"></div>
          </label>

          <!-- Delete button -->
          <button type="button" class="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors" data-action="click->client-assignments#delete" data-assignment-id="${assignment.id}" title="Remover atribuição">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
            </svg>
          </button>
        </div>
      </div>
    `
  }

  async toggle(event) {
    const assignmentId = event.target.dataset.assignmentId

    try {
      const response = await fetch(
        `/real_estate_admin/${this.realEstateAdminIdValue}/${this.rotationResourceValue}/${this.rotationIdValue}/toggle_client_assignment`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': this.csrfToken
          },
          body: JSON.stringify({
            assignment_id: assignmentId
          })
        }
      )

      const data = await response.json()

      if (data.status === 'success') {
        this.showSuccess(data.message)

        // Update UI to reflect the state change
        const assignmentElement = this.element.querySelector(`[data-assignment-id="${assignmentId}"]`)
        const brokerGroup = assignmentElement?.closest('[data-user-id]')

        if (assignmentElement) {
          if (data.enabled) {
            assignmentElement.classList.remove('opacity-50')
          } else {
            assignmentElement.classList.add('opacity-50')
          }

          // Update active count badge
          if (brokerGroup) {
            const groupContent = brokerGroup.querySelector('[data-client-assignments-target="groupContent"]')
            const badgeContainer = brokerGroup.querySelector('.bg-black.text-white')
            const allAssignments = groupContent.querySelectorAll('div[data-assignment-id]')
            let activeCount = 0

            allAssignments.forEach(el => {
              if (!el.classList.contains('opacity-50')) {
                activeCount++
              }
            })

            if (activeCount > 0) {
              if (badgeContainer) {
                badgeContainer.textContent = `${activeCount} ativo${activeCount === 1 ? '' : 's'}`
              } else {
                // Create badge if it doesn't exist
                const badgeHtml = `<span class="bg-black text-white text-xs font-bold px-2.5 py-1 rounded-full">${activeCount} ativo${activeCount === 1 ? '' : 's'}</span>`
                const expandIcon = brokerGroup.querySelector('[data-client-assignments-target="expandIcon"]')
                if (expandIcon) {
                  expandIcon.insertAdjacentHTML('beforebegin', badgeHtml)
                }
              }
            } else if (badgeContainer) {
              badgeContainer.remove()
            }
          }
        }
      } else {
        this.showError(data.message)
        // Revert the toggle
        event.target.checked = !event.target.checked
      }
    } catch (error) {
      console.error('Error toggling client assignment:', error)
      this.showError('Erro ao atualizar atribuição. Por favor, tente novamente.')
      // Revert the toggle
      event.target.checked = !event.target.checked
    }
  }

  async delete(event) {
    const assignmentId = event.currentTarget.dataset.assignmentId

    if (!confirm('Tem certeza que deseja remover esta atribuição de cliente?')) {
      return
    }

    try {
      const response = await fetch(
        `/real_estate_admin/${this.realEstateAdminIdValue}/${this.rotationResourceValue}/${this.rotationIdValue}/delete_client_assignment`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': this.csrfToken
          },
          body: JSON.stringify({
            assignment_id: assignmentId
          })
        }
      )

      const data = await response.json()

      if (data.status === 'success') {
        this.showSuccess(data.message)

        // Remove the assignment from UI with animation
        const assignmentElement = this.element.querySelector(`[data-assignment-id="${assignmentId}"]`)
        if (assignmentElement) {
          const brokerGroup = assignmentElement.closest('[data-user-id]')
          const groupContent = assignmentElement.closest('[data-client-assignments-target="groupContent"]')

          assignmentElement.style.opacity = '0'
          assignmentElement.style.transform = 'translateX(-20px)'
          assignmentElement.style.transition = 'opacity 0.3s, transform 0.3s'

          setTimeout(() => {
            assignmentElement.remove()

            // Update broker group counts if it still exists
            if (brokerGroup && groupContent) {
              const remainingAssignments = groupContent.querySelectorAll('div[data-assignment-id]')

              if (remainingAssignments.length === 0) {
                // Remove the entire group if no assignments left
                brokerGroup.style.opacity = '0'
                brokerGroup.style.transition = 'opacity 0.3s'
                setTimeout(() => brokerGroup.remove(), 300)
              } else {
                // Update the count
                const countElement = brokerGroup.querySelector('.text-sm.text-gray-500')
                if (countElement) {
                  const count = remainingAssignments.length
                  const phoneText = count === 1 ? 'telefone atribuído' : 'telefones atribuídos'
                  countElement.textContent = `${count} ${phoneText}`
                }

                // Update active count badge
                const badgeContainer = brokerGroup.querySelector('.bg-black.text-white')
                let activeCount = 0
                remainingAssignments.forEach(el => {
                  if (!el.classList.contains('opacity-50')) {
                    activeCount++
                  }
                })

                if (activeCount > 0) {
                  if (badgeContainer) {
                    badgeContainer.textContent = `${activeCount} ativo${activeCount === 1 ? '' : 's'}`
                  }
                } else if (badgeContainer) {
                  badgeContainer.remove()
                }
              }
            }

            // Check if list is empty now and show empty state
            if (this.hasListTarget && this.listTarget.children.length === 0) {
              this.listTarget.remove()

              // Show empty state
              const container = this.element.querySelector('[data-client-assignments-target="list"]')?.parentElement ||
                               this.element.querySelector('.mt-6')
              if (container) {
                container.innerHTML = `
                  <div class="text-center py-12" data-client-assignments-target="emptyState">
                    <svg class="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"/>
                    </svg>
                    <h3 class="text-lg font-medium text-gray-900 mb-2">Nenhuma atribuição de cliente</h3>
                    <p class="text-gray-500">Adicione atribuições para garantir que clientes específicos sejam direcionados aos corretores corretos.</p>
                  </div>
                `
              }
            }
          }, 300)
        }
      } else {
        this.showError(data.message)
      }
    } catch (error) {
      console.error('Error deleting client assignment:', error)
      this.showError('Erro ao remover atribuição. Por favor, tente novamente.')
    }
  }

  showSuccess(message) {
    this.showToast(message, 'success')
  }

  showError(message) {
    this.showToast(message, 'error')
  }

  showToast(message, type) {
    window.showNotification(message, type)
  }
}
